<template>
  <div class="product-like" :class="rootClasses">
    <button class="product-like__button" @click="openOverlay">
      <icon-heart class="product-like__heart-icon" :class="{ 'heart-beating': isBeating }" />
      {{ buttonText }}
    </button>
    <login-overlay
      v-if="showLogin"
      @login-successful="loadWishlistOptions"
      @cancel-close="closeOverlay">
      <p>You need to login to use Wishlists</p>
    </login-overlay>
    <custom-dialog
      v-else-if="showOverlay"
      :overlay-id="productId"
      :dialog-title="`Select a list for '${productName}'`"
      class="product-like__overlay"
      @dialog-closed="closeOverlay">
      <div v-if="loadingState === 1">
        Loading data...
      </div>
      <ul v-else class="product-like__list-container">
        <li v-for="wishlist in productStore.wishlists" :key="wishlist.id">
          <toggle-wishlist
            :wish-list="wishlist"
            :product-id="productId"
            @on-update="showNotification($event)" />
        </li>
      </ul>
      <parts-inline-notification v-if="messageText" :message-text="messageText" :message-type="messageType" disable-scroll />
      <create-wishlist :product-id="productId" @wishlist-created="closeOverlay" />
    </custom-dialog>
  </div>
</template>

<script lang="ts">
import IconHeart from '~/assets/icons/IconHeart.svg';
import CreateWishlist from "~/components/parts/product/CreateWishlist.vue";
import { useAuthStore } from '~/stores/auth';
import { useProductStore } from '~/stores/product';
import { defineAsyncComponent } from '#imports';

const CustomDialog = defineAsyncComponent(() => import('~/components/parts/CustomDialog.vue'));
const LoginOverlay = defineAsyncComponent(() => import('~/components/parts/login/LoginOverlay.vue'));
const ToggleWishlist = defineAsyncComponent(() => import('~/components/parts/product/ToggleWishlist.vue'));

export default {
  props: {
    includeText: Boolean,
    productId: [Number, String],
    productName: String,
    onWishlist: Boolean,
  },
  components: {
    CreateWishlist,
    ToggleWishlist,
    CustomDialog,
    LoginOverlay,
    IconHeart,
  },
  setup () {
    const authStore = useAuthStore();
    const productStore = useProductStore();

    return {
      authStore,
      productStore,
    };
  },
  data () {
    return {
      showOverlay: false,
      newListName: '',
      errorMessage: '',
      loadingState: 0,
      messageText: '',
      messageType: '',
      isBeating: false,
      wasOnListBeforeDialog: false,
    };
  },
  computed: {
    buttonText() {
      return this.includeText ? 'Add to Wishlist' : '';
    },
    rootClasses () {
      return {
        'product-like--selected': this.onList,
        'product-like--with-text': this.includeText,
      };
    },
    showLogin () {
      return this.showOverlay && !this.authStore.authenticated;
    },
    onList () {
      return this.productStore.wishlists.findIndex((wishlist) => wishlist.productIds?.includes(this.productId)) >= 0
    },
  },
  watch: {
    showLogin (doShow) {
      if (!doShow) {
        this.loadWishlistOptions();
      }
    },
  },
  methods: {
    openOverlay () {
      this.wasOnListBeforeDialog = this.onList;
      this.showOverlay = true;

      if (!this.showLogin) {
        this.loadWishlistOptions();
      }
    },
    closeOverlay () {
      this.showOverlay = false;
      
      // Trigger heartbeat only if product wasn't on list before and is now on list
      if (!this.wasOnListBeforeDialog && this.onList) {
        this.triggerHeartbeat();
      }
    },
    loadWishlistOptions () {
      if (this.loadingState !== 0 && !this.showLogin) {
        this.loadingState = 1;
        this.showOverlay = true;
        this.productStore.getWishlists()
          .then(() => {
            this.loadingState = 2;
          }).catch(() => {
            this.loadingState = -1;
          });
      }
    },
    showNotification(response) {
      if (response.message) {
        this.messageText = response.message
        this.messageType = response.status === 'SUCCESS' ? 'success' : 'error'
      }
    },
    triggerHeartbeat() {
      this.isBeating = true;
      setTimeout(() => {
        this.isBeating = false;
      }, 1000);
    }
  },
};
</script>

<style lang="scss">
@use "~/common/scss/form";
@use "~/common/scss/variables";

.product-like {
  $root: &;

  --icon-color: #{variables.$colorWhite};

  border: none;
  background: transparent;
  padding: .25rem .25rem;

  &--with-text {
    --icon-color: #{variables.$colorBlack};
  }

  &--selected {
    --icon-color: #{variables.$colorOrange};
    --icon-fill: #{variables.$colorOrange};
  }

  &__button {
    align-items: center;
    display: flex;
    gap: .25rem;
    justify-content: center;
    position: relative;
  }

  &__heart-icon {
    color: var(--icon-color);
    
    &.heart-beating {
      animation: heartbeat 1s ease-out;
    }
  }

  &__list-container {
    display: flex;
    flex-flow: column;
    list-style: none;
    margin-bottom: 1.5rem;
  }

  &__form {
    @include form.baseForm();

    input.check-box__input {
      display: none !important;
    }
  }

  * {
    box-sizing: border-box;
  }
}

@keyframes heartbeat {
  0% {
    transform: scale(1);
  }
  15% {
    transform: scale(1.25);
  }
  30% {
    transform: scale(1);
  }
  45% {
    transform: scale(1.15);
  }
  60% {
    transform: scale(1);
  }
  100% {
    transform: scale(1);
  }
}
</style>
